// « Sur cet écran » (D-091, maquette c1) : le « ? » de l'en-tête de chaque écran principal ouvre une
// feuille qui dit à quoi sert l'écran (selon le rôle), les gestes qu'on y fait, chacun avec
// « Montre-moi » (la visite guidée du même nom, src/guide/tours.ts), et le support en dernier recours.
// Chaque route de l'app a son entrée, onglet ou non (D-101) : vérifié par src/guide/guide.test.ts. Tutoiement ;
// l'espace partenaire vouvoie (D-074). Pas d'accord en dur : « on compte sur toi », pas « tu es attendue ».
// Ce que dit la feuille suit ce que la page montre à ce compte (`HelpCtx`) : les modules activés (D-122), un mineur (D-103),
// le vouvoiement d'un partenaire, aussi dans sa casquette Bénévole (D-103), la casquette Bénévole d'un compte qui a un autre
// rôle (D-132 : ni agenda ni bannière), l'abonné (D-110). `leadFor` compose le texte ; `when` garde un geste que la page montre.
import type { HatKey, Persona } from '../ui/context';
import type { ModuleKey } from '../domain/modules';

export interface ScreenGesture {
  title: string;
  sub: string;
  /** La visite « Montre-moi » (sinon : le tuto, s'il y en a un). */
  tour?: string;
  tuto?: string;
  /** Rôles pour qui le geste a un sens (par défaut : tous ceux de l'écran). */
  for?: ScreenRole[];
  /** Seulement pour qui a cette casquette (la casquette Bénévole n'est pas celle des mineurs, D-097). */
  hat?: HatKey;
  /** Pas pour un compte mineur (D-103 : ni groupe de bénévoles, ni offre de partenaire). */
  adultOnly?: boolean;
  /** Seulement quand la page montre ce que le geste fait faire (une ligne, une tuile, une carte). */
  when?: (x: HelpCtx) => boolean;
  /** Le sous-titre d'après ce que la page montre (« Quelqu’un qui veut jouer, un bénévole »). */
  subFor?: (x: HelpCtx) => string;
  /** Au vouvoiement (un compte partenaire, D-103). */
  vous?: { title?: string; sub?: string };
}
export interface ScreenHelp {
  /** Nom de l'écran (au-dessus de l'explication, avec l'équipe s'il y a lieu). */
  label: string;
  /** À quoi sert l'écran, selon le rôle (`default` sinon). */
  lead: Partial<Record<ScreenRole | 'default', string>>;
  gestures: ScreenGesture[];
  /** L'écran parle d'une équipe : son nom s'ajoute au libellé. */
  team?: boolean;
  /** Le nom de l'équipe seulement quand l'adresse la nomme (#/matchs/<équipe>) : sinon l'écran montre tout le club. */
  teamInRoute?: boolean;
  /** Pour un compte mineur (D-103) : ce que dit l'écran, sans offre de partenaire ni groupe. */
  minorLead?: string;
  /** Au vouvoiement : un compte partenaire hors de son espace (sa casquette Bénévole, D-103). */
  vous?: string;
  /** Ce que dit l'écran, composé d'après ce qu'il montre à ce compte ; rien : `minorLead`, `vous`, puis `lead`. */
  leadFor?: (x: HelpCtx) => string | undefined;
}

/** Le rôle de l'écran : celui de la casquette, « technique » pour l'espace du responsable technique (D-093). */
export type ScreenRole = Persona | 'technique';

/** Ce que l'aide sait du compte, pour dire ce que la page lui montre vraiment. */
export interface HelpCtx {
  role: ScreenRole;
  /** Un compte mineur (D-103). */
  minor: boolean;
  /** Un compte partenaire : vouvoiement partout, aussi dans sa casquette Bénévole (D-103). */
  vous: boolean;
  /** Le module est activé par le club (D-122). */
  on: (k: ModuleKey) => boolean;
  /** Mes casquettes ; la casquette affichée est-elle Bénévole ? A-t-on un autre rôle qu'elle (D-132) ? */
  hats: HatKey[];
  benevoleHat: boolean;
  otherHat: boolean;
  /** Des enfants au club. */
  parent: boolean;
  /** Je joue dans une équipe (toutes casquettes) : « Quelqu’un qui veut jouer » sur Inviter. */
  player: boolean;
  /** Moi ou mes enfants jouons (toutes casquettes) : les indisponibilités dans Moi. */
  onTeam: boolean;
  /** J'entraîne une équipe (toutes casquettes) : « Inviter ton équipe ». */
  coach: boolean;
  /** Une équipe dans la casquette affichée : « Mon équipe », le filtre de Matchs. */
  team: boolean;
  /** La tuile « Inviter » d'un ami (coach, ou joueuse dans la casquette affichée), comme le Club (club-hub.ts). */
  friend: boolean;
  /** Un compte adulte du club (ni mineur, ni partenaire, ni abonné) : « Qui contacter », Mes trajets. */
  adult: boolean;
}

/** « a, b et c ». */
export const and = (xs: (string | false | null | undefined | 0)[]): string => {
  const l = xs.filter((s): s is string => !!s);
  return l.length < 2 ? l.join('') : `${l.slice(0, -1).join(', ')} et ${l[l.length - 1]}`;
};
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Ce que dit la feuille « Sur cet écran » à ce compte. */
export function leadOf(help: ScreenHelp, x: HelpCtx): string {
  const own = help.leadFor?.(x);
  if (own) return own;
  if (x.minor && help.minorLead) return help.minorLead;
  if (x.vous && x.role !== 'partenaire' && help.vous) return help.vous;
  return help.lead[x.role] ?? help.lead.default ?? '';
}

/** Les gestes que la page montre à ce compte (rôle, casquette, mineur, `when`), au vouvoiement s'il y a lieu ; sans le
 *  filtre des modules de leur visite (src/guide/screen-help.ts). */
export function gesturesOf(help: ScreenHelp, x: HelpCtx): ScreenGesture[] {
  return help.gestures.filter(g => (!g.for || g.for.includes(x.role)) && (!g.hat || x.hats.includes(g.hat)) && !(x.minor && g.adultOnly) && (!g.when || g.when(x)))
    .map(g => ({ ...g, title: (x.vous && g.vous?.title) || g.title, sub: (x.vous && g.vous?.sub) || g.subFor?.(x) || g.sub }));
}

// --- Les textes composés d'après la page -----------------------------------------------------------------------
/** Accueil de la casquette Bénévole (D-097) : l'agenda et les partenaires seulement pour qui n'a pas d'autre accueil (D-132). */
const supporterHomeLead = (x: HelpCtx): string => {
  const tail = and(['les matchs du club', !x.otherHat && x.on('agenda') && 'l’agenda', !x.otherHat && x.on('partenaires') && 'les partenaires', 'les annonces']);
  return x.vous ? `Côté bénévole : votre prochain créneau, votre prochain cap et vos badges, ce dont le club a besoin, votre groupe ; ${tail}.`
    : `Côté bénévole : ton prochain créneau, ton prochain cap et tes badges, ce dont le club a besoin, ton groupe ; ${tail}.`;
};
/** Inviter (D-075, D-122) : ce que la page propose à ce compte (invite-me.ts ; dans la casquette Bénévole, pas l'équipe). */
const inviteWhat = (x: HelpCtx): string[] => [x.coach && !x.benevoleHat && (x.vous ? 'votre équipe' : 'ton équipe'), x.player && 'quelqu’un qui veut jouer',
  x.on('benevolat') && !x.minor && 'un bénévole'].filter((s): s is string => !!s);
/** La tuile « Inviter » du Club (club-hub.ts) : un ami (coach, joueuse) ou un bénévole ; jamais pour un mineur. */
const inviteTile = (x: HelpCtx) => !x.minor && (x.on('benevolat') || x.friend);
/** Club des membres (maquette m7) : ses rubriques, d'après les modules et le compte (club-hub.ts). */
const memberClubLead = (x: HelpCtx): string => {
  const aider = [x.on('benevolat') && !x.benevoleHat && x.role !== 'supporter' && 'bénévolat', inviteTile(x) && 'inviter',
    !x.minor && x.on('partenaires') && 'trouver un partenaire'].filter(Boolean);
  if (x.role === 'supporter') {
    const vie = and([x.on('agenda') && 'l’agenda', 'les annonces', 'les matchs du club', x.on('partenaires') && 'les partenaires']);
    return `La vie du club (${vie})${aider.length ? `, aider le club (${aider.join(', ')})` : ''}${x.adult ? ', écrire au bureau' : ''}${x.on('page') ? ' ; en bas, la page publique' : ''}.`;
  }
  const mine = ['matchs et résultats', x.on('defis') && 'défis et gages', x.on('prepa') && 'prépa'].filter(Boolean).join(', ');
  const rest = and(['la vie du club', aider.length && `aider le club (${aider.join(', ')})`, x.adult && 'le bureau']);
  return x.team ? `${x.role === 'parent' ? 'L’équipe' : 'Ton équipe'} d’abord (${mine}), puis ${rest}.` : `${cap(rest)}.`;
};
/** Moi : ce qui est sur la page de ce compte (me.ts, recus-entries.ts). */
const meLead = (x: HelpCtx): string => {
  if (x.role === 'abonne') return 'Tes notifications, ton compte, et « Rejoindre le club » : le bureau t’accueille.';
  const t = (tu: string, vous: string) => (x.vous ? vous : tu);
  const partnerOnly = x.role === 'partenaire';
  const items = [x.on('benevolat') && t('tes badges', 'vos badges'), !partnerOnly && x.on('avantages') && t('tes avantages', 'vos avantages'),
    !partnerOnly && x.on('page') && t('tes suivis', 'vos suivis'), x.adult && x.on('benevolat') && t('tes trajets', 'vos trajets'),
    !partnerOnly && x.onTeam && 'les indisponibilités', 'les notifications'];
  if (partnerOnly) return cap(`${and([...items, x.on('partenaires') && 'les entreprises que vous présentez au club', 'votre compte'])}.`);
  const club = [inviteTile(x) && 'inviter', !x.minor && x.on('partenaires') && 'trouver un partenaire'].filter((s): s is string => !!s);
  return `${t('Ce qui est à toi', 'Ce qui est à vous')} : ${and([...items, t('ton compte', 'votre compte')])}.${club.length ? ` ${cap(and(club))} ${club.length > 1 ? 'sont' : 'est'} dans Club › Aider le club.` : ''}`;
};

/** La clé d'une route : son premier segment (« » pour l'accueil) ; #/rt/… : l'onglet du responsable technique ;
 *  #/plays/… : l'onglet Basket ; #/club/<page> : sa propre aide quand elle en a une (D-132, carte P-19). */
export function screenKey(hash: string): string {
  const [a = '', b, c] = hash.replace(/^#\/?/, '').split('/');
  if (a === 'plays') return 'basket';
  // D-139 : la page de réglages de chaque module (ouverte par sa ⚙), une rubrique comprise (#/salles/reglages/zone).
  if (b === 'reglages' && SCREENS[`${a}/reglages`]) return `${a}/reglages`;
  if (a === 't' && c === 'reglages') return 't/reglages';   // D-148 : la ⚙ de l'équipe
  if (a === 'club' && b === 'page' && c === 'reglages') return 'club/page/reglages';
  if (a === 'club' && b === 'site' && c && SCREENS[`club/site/${c}`]) return `club/site/${c}`;   // D-140 : les écrans du site
  if (a === 'club' && b === 'modules' && c === 'assistant') return 'club/modules/assistant';   // D-158 : l'assistant d'un module
  if (a === 'club' && b && SCREENS[`club/${b}`]) return `club/${b}`;   // Club › Bénévolat (D-127), Modules, Le bureau…
  if (a === 'benevoles' && b === 'groupe') return 'benevoles/groupe';   // la page d'un groupe de bénévoles (B15)
  if (a === 'derogations' && b) return b === 'reglages' ? 'derogations/reglages' : 'derogations/fiche';   // D-137 : une dérogation, les réglages
  if (a === 'formation' && b) return b === 'valider' || b === 'officiels' ? `formation/${b}` : 'formation/parcours';   // D-141
  if (a === 'mains-vives') return b && SCREENS[`mains-vives/${b}`] ? `mains-vives/${b}` : 'mains-vives';   // D-164
  if (a === 'bilan' && b === 'suivi') return 'bilan/suivi';
  if (a === 'licences' && b === 'signatures') return 'licences/signatures';   // D-169 : qui a signé quoi
  if (a === 'licences' && b === 'redaction') return 'licences/redaction';   // D-172 : l'éditeur d'un document
  if (a === 'moi' && b === 'skins') return 'moi/skins';   // D-149 : Mes skins   // D-143 : la prévision du bureau ; le reste : le bilan du licencié
  // Les écrans des salles (K6) : chacun son aide — une salle (son identifiant), les gestionnaires, un créneau perdu…
  if (a === 'salles' && b) {
    const k = /^[0-9a-f-]{36}$/i.test(b) ? 'salles/salle' : b === 'gestionnaire' ? 'salles/gestionnaires'
      : b === 'demande' || b === 'demander' ? 'salles/salles' : `salles/${b}`;
    if (SCREENS[k]) return k;
  }
  if (a !== 'rt') return a;
  return `rt/${b === 'seance' || !b ? 'equipes' : b === 'joueur' ? 'joueurs' : b === 'coach' ? 'coachs' : b}`;
}

export const SCREENS: Record<string, ScreenHelp> = {
  '': {
    label: 'Accueil',
    lead: {
      bureau: 'Le week-end du club et les bénévoles qui manquent ; « À faire cette semaine », une décision avant une information (le reste derrière « Tout voir ») ; le club en chiffres, ce qui vient. Chaque ligne s’ouvre sur le bon écran.',
      coach: 'Ta prochaine séance en grand, qui vient et qui manque, et les gestes juste dessous ; ce qui attend ta décision ; le prochain match et les chiffres de ton équipe.',
      joueur: 'Ta semaine en grand : on compte sur toi par défaut, préviens seulement si tu ne peux pas venir. Puis ce qui attend un geste, et les chiffres de ton équipe.',
      parent: 'La semaine de tes enfants : ils sont attendus par défaut, préviens seulement en cas d’absence. Puis ce qui attend un geste (le dossier, le ressenti, le bilan).',
      partenaire: 'Ce que le club vous demande, vos invitations, votre visibilité et vos emplacements.',
      technique: 'La semaine de toutes les équipes, ce qui attend ta décision, la présence et le week-end, chaque équipe.',
      supporter: 'Côté bénévole : ton prochain créneau, ton prochain cap et tes badges, ce dont le club a besoin, ton groupe ; les matchs du club, l’agenda, les partenaires et les annonces.',
      abonne: 'Les matchs de toutes les équipes du club ; une étoile pour suivre une équipe, ses résultats sur ton téléphone.',
      default: 'Les matchs du club, les créneaux de bénévoles et les annonces.',
    },
    leadFor: x => (x.role === 'supporter' ? supporterHomeLead(x) : undefined),
    gestures: [
      { title: 'Mobiliser les bénévoles', sub: 'Un appel prêt à envoyer', tour: 'demander-benevoles', for: ['bureau'] },
      { title: 'Relancer les dossiers de licence', sub: 'Chaque famille reçoit ce qui lui manque', tour: 'dossiers-licence', for: ['bureau'] },
      { title: 'Traiter les demandes d’essai', sub: 'Dans l’effectif, essai d’abord, ou refuser', tour: 'demandes', for: ['bureau', 'coach'] },
      { title: 'Faire l’appel', sub: 'Présences, absences, retards', tour: 'faire-appel', for: ['coach'] },
      { title: 'Préparer le plan de séance', sub: 'Exercices du club, durées', tour: 'plan-seance', for: ['coach'] },
      { title: 'Convoquer pour le match', sub: 'Composition, renforts', tour: 'convoquer-match', for: ['coach'] },
      { title: 'Le chronomètre et les drills', sub: 'Le bouton ⏱ en haut, sur le terrain', tour: 'chronometre', for: ['coach'] },
      { title: 'Prévenir d’une absence', sub: 'Un geste, le coach est prévenu', tour: 'prevenir-absence', for: ['joueur', 'parent'] },
      { title: 'Après la limite, « Tout va bien ? »', sub: 'La raison d’un toucher, le coach est prévenu', tuto: 'desistement-tardif', for: ['joueur', 'parent'] },
      { title: 'Faire le point', sub: 'Au 3e désistement tardif, sans sanction', tuto: 'desistements-equipe', for: ['coach'] },
      { title: 'Répondre Incertaine', sub: 'Pas sûr de venir : une relance', tour: 'incertaine', for: ['joueur', 'parent'] },
      // La carte « Tu connais quelqu’un chez… ? » : les adultes du club (jamais un mineur, ni un compte partenaire, D-103) ; dans la
      // casquette Bénévole, seulement pour qui n'a pas d'autre accueil (D-132).
      { title: 'Qui connaît ?', sub: '👍 je peux présenter, 👎 la suivante', tour: 'qui-connait', for: ['joueur', 'parent', 'coach', 'supporter'], adultOnly: true,
        when: x => !x.vous && (x.role !== 'supporter' || !x.otherHat) },
      { title: 'Activer les notifications', sub: 'Convocations, rappels la veille', tour: 'notifications', for: ['joueur', 'parent', 'supporter', 'membre'] },
      { title: 'Répondre à un appel du club', sub: 'Un lot, un coup de main', tour: 'partenaire-appel', for: ['partenaire'] },
      { title: 'Offrir l’app au club', sub: 'Une promesse de don, votre logo chez chaque membre', tour: 'partenaire-app-offerte', for: ['partenaire'] },
      { title: 'Valider l’avantage d’un membre', sub: 'Scanner son QR, ou taper le code', tour: 'valider-avantage', for: ['partenaire'] },
      { title: 'Prendre un créneau de bénévole', sub: 'Buvette, table de marque', tour: 'prendre-creneau', for: ['supporter', 'membre'] },
      { title: 'Encourager une équipe', sub: 'Je viens voir, un mot avant le match', tour: 'encourager', for: ['supporter'] },
      { title: 'Rejoindre un groupe', sub: 'Les cuistos, les gros bras…', tour: 'groupe-benevoles', for: ['supporter'] },
      { title: 'Ta casquette Bénévole', sub: 'Créneaux, groupe, badges, matchs du club', tour: 'casquette-benevole', for: ['joueur', 'parent', 'coach'], hat: 'benevole' },
      { title: 'Mes badges', sub: 'Parcours, grades, récompenses', tour: 'mes-badges', for: ['supporter'] },
      { title: 'Écrire le projet sportif', sub: 'Fil rouge, objectifs du cycle', tour: 'pp-rt-projet', for: ['technique'] },
      { title: 'Lancer une évaluation', sub: 'Les coachs notent, tu vois tout', tour: 'pp-rt-eval', for: ['technique'] },
    ],
  },
  'rt/equipes': {
    label: 'Équipes',
    lead: { default: 'Toutes les équipes : coach, niveau, effectif, séances par semaine, présence ; le suivi des séances sur six semaines, et la constitution des équipes.' },
    gestures: [{ title: 'La visite du responsable technique', sub: 'Accueil, séances, projet, évaluations', tour: 'demo-technique' }],
  },
  'rt/projet': {
    label: 'Projet sportif',
    lead: { default: 'Les cycles de la saison, le fil rouge de chaque tranche d’âge, les objectifs reliés aux exercices du club ; le thème du mois proposé aux coachs.' },
    gestures: [
      { title: 'Écrire le projet sportif', sub: 'Fil rouge, objectifs du cycle', tour: 'pp-rt-projet' },
      { title: 'Publier le thème du mois', sub: 'Une proposition aux coachs', tour: 'pp-rt-theme' },
    ],
  },
  'rt/joueurs': {
    label: 'Joueurs',
    lead: { default: 'Les évaluations par campagne, les joueurs à suivre, les surclassements à valider. Visible du staff seulement.' },
    gestures: [{ title: 'Lancer une campagne d’évaluation', sub: 'Les catégories, les dates', tour: 'pp-rt-eval' }],
  },
  'rt/coachs': {
    label: 'Coachs',
    lead: { default: 'L’encadrement de chaque équipe, les diplômes des coachs et les obligations par niveau.' },
    gestures: [{ title: 'Diplômes et obligations', sub: 'BF, CQP, DE JEPS ; le modèle de la ligue', tour: 'pp-rt-diplomes' }],
  },
  planning: {
    label: 'Planning', team: true,
    lead: {
      coach: 'Tes séances et tes matchs des 3 prochaines semaines, avec qui vient. Touche une séance pour faire l’appel ou préparer le plan.',
      bureau: 'La semaine de toutes les équipes : séances et matchs, avec les présences prévues. Touche-en un pour la fiche ; une équipe, pour sa page.',
      default: 'Les séances et les matchs des 3 prochaines semaines. Touche-en un pour le détail, ou pour prévenir d’une absence.',
    },
    gestures: [
      { title: 'Programmer une séance', sub: 'Une fois ou toutes les semaines', tour: 'programmer', for: ['coach', 'bureau'] },
      { title: 'Programmer dans un créneau', sub: 'Les créneaux du club, ou « Ailleurs »', tour: 'programmer-creneau', for: ['coach'] },
      { title: 'Faire l’appel', sub: 'Présences, absences, retards', tour: 'faire-appel', for: ['coach', 'bureau'] },
      { title: 'Préparer le plan de séance', sub: 'Exercices du club, durées', tour: 'plan-seance', for: ['coach'] },
      { title: 'Annuler une séance', sub: 'L’équipe est prévenue tout de suite', tour: 'annuler-seance', for: ['coach', 'bureau'] },
      { title: 'Prévenir d’une absence', sub: 'Le coach est prévenu', tour: 'prevenir-absence', for: ['joueur', 'parent'] },
      { title: 'Une indisponibilité', sub: 'Blessure, vacances, examens', tour: 'indisponibilite', for: ['joueur', 'parent'] },
    ],
  },
  t: {
    label: 'Équipe', team: true,
    lead: {
      coach: 'L’effectif, les accès de chacun, le calendrier FFBB de l’équipe.',
      bureau: 'L’équipe : l’effectif, les matchs, les résultats. Pour la renommer ou la supprimer : Club › Équipes et coachs › « Modifier ».',
      default: 'L’équipe : l’effectif, les matchs, les résultats.',
    },
    gestures: [
      { title: 'Renommer ou supprimer l’équipe', sub: 'Club › Équipes et coachs › « Modifier »', tuto: 'saison-equipes', for: ['bureau'] },
      { title: 'Inviter ton équipe dans l’app', sub: 'Un lien par joueuse, par WhatsApp', tour: 'inviter-equipe', for: ['coach'] },
      { title: 'Relier l’équipe à la FFBB', sub: 'Matchs, salles et scores tout seuls', tuto: 'relier-ffbb', for: ['coach', 'bureau'] },
      { title: 'Les indisponibilités de l’équipe', sub: 'Chaque semaine, périodes', tour: 'indispos-equipe', for: ['coach'] },
      { title: 'La limite pour prévenir d’une absence', sub: 'La ⚙ : chaque semaine, ou avant chaque rendez-vous', tour: 'prevenir-a-temps', for: ['coach'] },
      { title: 'La limite pour prévenir d’une absence', sub: 'La ⚙ de l’équipe', tuto: 'prevenir-a-temps', for: ['bureau'] },
      { title: 'Les accords des parents', sub: 'Une icône photo, une icône voiture', tour: 'accords-effectif', for: ['coach', 'bureau'] },
      { title: 'La fiche pour les tirages', sub: 'Niveau et postes, staff seulement', tour: 'fiche-tirage', for: ['coach'] },
    ],
  },
  // D-148 : la ⚙ de la page de l'équipe.
  't/reglages': {
    label: 'Réglages de l’équipe', team: true,
    lead: { default: '« Prévenir d’une absence » : la limite de l’équipe (chaque semaine, ou un délai avant chaque rendez-vous ; par défaut 24 h avant une séance, 48 h avant un match) et ce que lit l’équipe la veille. « Désistements tardifs » : la carte « Faire le point » au 3e de la saison, le message après une absence sans prévenir.' },
    gestures: [
      { title: 'Régler la limite', sub: 'Chaque semaine, ou avant chaque rendez-vous', tour: 'prevenir-a-temps' },
      { title: 'Désistements tardifs', sub: 'Prévenu tard, sans prévenir, faire le point', tuto: 'desistements-equipe', for: ['coach'] },
    ],
  },
  basket: {
    label: 'Basket', team: true,
    lead: { coach: 'Les plays de l’équipe, les exercices et les séances types du club, la prépa physique. Drills et jeux : la séance qui vient et ce qu’elle a à lancer, les outils du terrain (chrono, tirage, table de marque), les jeux (Le Parcours, Le Sablier), la bibliothèque des drills par usage, « + Créer » et « Mes créations ».', default: 'Les plays de l’équipe, animés temps par temps.' },
    gestures: [
      { title: 'Lancer un drill', sub: 'La bibliothèque par usage, une fiche par drill', tour: 'lancer-drill', for: ['coach'] },
      { title: 'Mes créations', sub: 'Créer, modifier, dupliquer, supprimer', tour: 'mes-creations', for: ['coach'] },
      { title: 'Le tirage, outil du terrain', sub: 'Des équipes justes, sans exercice', tour: 'tirage-outil', for: ['coach'] },
      { title: 'Le Parcours', sub: 'Un exercice devient une partie', tour: 'jouer-parcours', for: ['coach'] },
      { title: 'Le Sablier', sub: 'Chaque équipe a sa réserve de temps', tour: 'jouer-sablier', for: ['coach'] },
      { title: 'Rejoindre avec un téléphone', sub: 'Un QR par équipe, le tien en télécommande', tour: 'rejoindre-telephone', for: ['coach'] },
      { title: 'Le chronomètre', sub: 'Score, possession, temps-morts', tour: 'chronometre', for: ['coach'] },
      { title: 'Programmer une séquence', sub: 'Un son, une attente, un signal, répétés', tour: 'drill-sequence', for: ['coach'] },
      { title: 'Les signes de l’équipe', sub: 'Chaque geste, son sens chez vous', tour: 'signes-equipe', for: ['coach'] },
      { title: 'Proposer Mains vives', sub: 'Le jeu caméra, à faire chez soi', tour: 'mains-vives-proposer', for: ['coach'] },
      { title: 'Mains vives', sub: 'Le jeu caméra, seul avec ton téléphone', tour: 'mains-vives', for: ['joueur'] },
      { title: 'Créer un play', sub: 'Dessiner, animer, montrer à l’équipe', tour: 'creer-play', for: ['coach'] },
      { title: 'Voir un play', sub: 'En 2D, en 3D, ses variantes', tour: 'voir-play' },
      { title: 'La prépa physique', sub: 'Un programme à trois niveaux', tour: 'prepa-coach', for: ['coach'] },
      { title: 'Ma séance de prépa', sub: 'Valider, gagner des points', tour: 'prepa-joueur', for: ['joueur'] },
    ],
  },
  licences: {
    label: 'Licences',
    lead: { default: 'Les dossiers de la saison : licence FFBB, médical, pièces, cotisation et échéancier de chaque licencié. Les tarifs par équipe et les pièces demandées : la ⚙, en haut à droite.' },
    gestures: [
      { title: 'Importer les licenciés', sub: 'L’export Excel de FBI', tour: 'importer-licencies' },
      { title: 'Relancer les dossiers incomplets', sub: 'Chaque famille reçoit ce qui lui manque', tour: 'dossiers-licence' },
    ],
  },
  agenda: {
    label: 'Agenda du club',
    lead: { bureau: 'Tournois, lotos, AG : les inscriptions, les bénévoles et les partenaires sollicités.', default: 'Tournois, lotos, AG : dis si tu viens, et avec combien de personnes.' },
    vous: 'Tournois, lotos, AG : dites si vous venez, et avec combien de personnes.',
    gestures: [
      { title: 'Dire si tu viens', sub: 'Combien, sous quel prénom', tour: 'agenda', vous: { title: 'Dire si vous venez' } },
      { title: 'Demander des bénévoles', sub: 'Des créneaux, chacun prend sa place', tour: 'demander-benevoles', for: ['bureau'] },
    ],
  },
  benevoles: {
    label: 'Bénévolat',
    lead: { bureau: 'Le quotidien du bénévolat : les créneaux à pourvoir (matchs, événements, appels), les groupes de bénévoles et les badges (nouveaux grades à féliciter, piliers de la saison). Les réglages : la ⚙, en haut à droite.', default: 'Les créneaux à prendre : chaque créneau tenu fait monter un parcours de tes badges. Les groupes de bénévoles : une identité, un fil, un objectif commun ; une mission du groupe tous les 60 jours garde sa place.' },
    minorLead: 'Les créneaux que le club ouvre aux jeunes : chaque créneau tenu fait monter un parcours de tes badges.',
    vous: 'Les créneaux à prendre : chaque créneau tenu fait monter un parcours de vos badges. Les groupes de bénévoles : une identité, un fil, un objectif commun ; une mission du groupe tous les 60 jours garde sa place.',
    gestures: [
      { title: 'Demander des bénévoles', sub: 'Un appel avec ses créneaux', tour: 'demander-benevoles', for: ['bureau'] },
      { title: 'Les besoins des matchs', sub: 'Par équipe, ou un seul match', tour: 'besoins-matchs', for: ['bureau'] },
      { title: 'Inscrire quelqu’un', sub: 'Un oui de vive voix ; retirer avec un merci', tour: 'inscrire-quelquun', for: ['bureau'] },
      { title: 'Féliciter, donner une étoile', sub: 'Nouveaux grades, piliers, récompenses', tour: 'badges-bureau', for: ['bureau'] },
      { title: 'Prendre un créneau', sub: 'Buvette, table de marque', tour: 'prendre-creneau' },
      { title: 'Rejoindre un groupe', sub: 'Les cuistos, les gros bras…', tour: 'groupe-benevoles', adultOnly: true },
      { title: 'Créer un groupe', sub: 'Tu en es le chef, 30 jours pour le lancer', tour: 'creer-groupe', adultOnly: true, vous: { sub: 'Vous en êtes le chef, 30 jours pour le lancer' } },
      { title: 'Mener son groupe', sub: 'Identité, demandes, pause', tour: 'chef-groupe', adultOnly: true },
      { title: 'Objectifs et challenge des groupes', sub: 'La récompense au choix du bureau', tour: 'groupes-bureau', for: ['bureau'] },
      { title: 'Valider les reçus fiscaux', sub: 'Frais de route laissés au club', tour: 'recus-benevoles', for: ['bureau'] },
    ],
  },
  'benevoles/groupe': {
    label: 'Un groupe de bénévoles',
    lead: {
      bureau: 'Un groupe de bénévoles : son identité, ses membres, son objectif du trimestre, ses demandes et son fil. Le chef répartit ; le bureau garde la main.',
      default: 'Ton groupe de bénévoles : ta place, l’objectif du trimestre, les demandes confiées au groupe et son fil. Une mission du groupe garde ta place.',
    },
    minorLead: 'Un groupe de bénévoles du club : son identité et ce qu’il fait pour le club.',
    gestures: [
      { title: 'Les groupes de bénévoles', sub: 'Rejoindre, l’essai, sa place', tour: 'groupe-benevoles', adultOnly: true },
      { title: 'Mener son groupe', sub: 'Identité, demandes, pause', tour: 'chef-groupe', adultOnly: true },
      { title: 'Objectifs et challenge des groupes', sub: 'La récompense au choix du bureau', tour: 'groupes-bureau', for: ['bureau'] },
    ],
  },
  'licences/reglages': {
    label: 'Réglages des licences',
    lead: { default: 'Ce qui se règle en début de saison : le tarif de chaque équipe (la cotisation de chacun en découle), les pièces que le club demande dans chaque dossier, les documents à signer en ligne (rédigés dans l’app ou déposés en PDF, 5 actifs au plus, et qui signe), les accords des parents.' },
    gestures: [{ title: 'Relancer les dossiers incomplets', sub: 'Chaque famille reçoit ce qui lui manque', tour: 'dossiers-licence' },
      { title: 'Rédiger un document à signer', sub: 'Un modèle ou une page blanche, l’encart Signature', tour: 'documents-rediger' },
      { title: 'Rédiger, déposer, publier', sub: 'L’éditeur, le PDF, les versions', tuto: 'documents-club' },
      { title: 'Suivre les signatures', sub: 'Qui a signé quoi, « Relancer »', tour: 'documents-signes' }],
  },
  enfants: {
    label: 'Mes enfants',
    lead: { default: 'Pour chaque enfant : son équipe, son prochain rendez-vous, ses accords (photos, transport), son dossier et ses documents signés, son code perso.' },
    gestures: [
      { title: 'Ses accords', sub: 'Les photos, le transport : « Modifier » quand tu veux', tour: 'accords-enfant' },
      { title: 'Signer les documents du club', sub: 'Lus dans l’app, signés en ligne', tuto: 'documents-signer', when: x => x.on('licences') },
      { title: 'Le code perso', sub: 'Pour ouvrir l’app sans e-mail', tour: 'code-perso' },
      { title: 'Prévenir d’une absence', sub: 'Le coach est prévenu', tour: 'prevenir-absence' },
      { title: 'Mains vives', sub: 'La séance de ton enfant passe par ton téléphone', tour: 'mains-vives-enfant' },
    ],
  },
  // D-169 : les accords des parents et les documents signés en ligne.
  accords: {
    label: 'Les accords',
    lead: { default: 'Deux questions pour ton enfant : les photos (oui, seulement dans l’app, non) et le transport (toi seulement, les parents de l’équipe, tout adulte du covoiturage). Un parent suffit ; si l’autre répond autrement, le choix le plus protecteur s’applique.',
      joueur: 'Les photos du club : oui, seulement dans l’app, ou non. Tu choisis pour toi, et tu changes d’avis quand tu veux.' },
    minorLead: 'Tes accords pour les photos et le transport : tes parents les donnent et les changent.',
    gestures: [
      { title: 'Ses accords', sub: 'Répondre, puis « Modifier » quand tu veux', tour: 'accords-enfant', for: ['parent'] },
      { title: 'Une place proposée', sub: '« D’accord » ou « Non merci »', tuto: 'covoit-accords' },
    ],
  },
  dossier: {
    label: 'Dossier de licence',
    lead: { default: 'Le dossier de la saison en quatre étapes : les accords, les documents à signer (lus dans l’app, signés en ligne), la cotisation, la licence FFBB. À faire quand tu veux, dans l’ordre que tu veux.' },
    gestures: [
      { title: 'Signer les documents du club', sub: 'Ton nom, la case, « Signer »', tuto: 'documents-signer' },
      { title: 'Ses accords', sub: 'Les photos, le transport', tour: 'accords-enfant', for: ['parent'] },
    ],
  },
  'photos-a-revoir': {
    label: 'Photos à revoir',
    lead: { default: 'Un parent a resserré l’accord photo de son enfant : les photos de l’équipe déjà publiées sont à revoir. Arceau ne sait pas lesquelles le montrent : regarde, et retire d’un geste.' },
    gestures: [{ title: 'Publier un selfie de victoire', sub: 'Le message, la case à cocher', tuto: 'selfie-accords', for: ['coach', 'bureau'] }],
  },
  'licences/redaction': {
    label: 'L’éditeur d’un document',
    lead: { default: 'Le document du club, comme sur une feuille : Titre, Paragraphe, Liste, Gras ; « Insérer » place l’encart Signature au curseur ou un champ rempli tout seul (les étiquettes vertes). Touche l’encart pour ses cases d’autorisation et sa place. Enregistré tout seul, les familles ne voient rien avant « Aperçu » puis « Publier ». Une personne du bureau à la fois.' },
    gestures: [
      { title: 'Rédiger, déposer, publier', sub: 'L’éditeur, l’encart, les versions', tuto: 'documents-club' },
      { title: 'Rédiger un document à signer', sub: 'Depuis les réglages des licences', tour: 'documents-rediger' },
    ],
  },
  'licences/signatures': {
    label: 'Documents signés',
    lead: { default: 'Qui a signé quoi, version par version : les barres de la saison (ceux qui ont signé l’ancienne version seulement), ceux qui n’ont pas encore signé (« Relancer », une fois par jour ; « Sur papier » sans compte parent), les dernières signatures et leur récépissé, la conservation et « Exporter ».' },
    gestures: [
      { title: 'Suivre et relancer les signatures', sub: 'Une notification et un e-mail', tour: 'documents-signes' },
      { title: 'Déposer un document à signer', sub: 'Un PDF, qui signe', tuto: 'documents-club' },
    ],
  },
  // Réglages · Bénévolat (D-127 ; D-139 : la ⚙ de l'onglet Bénévolat, une page en rubriques repliables).
  'benevoles/reglages': {
    label: 'Réglages du bénévolat',
    lead: { default: 'Tout ce qui se règle pour les bénévoles, sur une page : les besoins des matchs, les parcours des badges, les noms de créneaux à ranger, les récompenses des grades, les créneaux ouverts aux jeunes, les groupes, les annonces et relances. Touche une rubrique pour l’ouvrir.' },
    gestures: [
      { title: 'Les réglages du bénévolat', sub: 'Huit rubriques sur une page, chacune son résumé', tour: 'reglages-benevolat' },
      { title: 'Les besoins des matchs', sub: 'Par équipe, ou un seul match', tour: 'besoins-matchs' },
      { title: 'Créer un parcours', sub: 'Un nom, un picto, les créneaux qui comptent', tour: 'creer-parcours' },
      { title: 'Ouvrir un créneau aux jeunes', sub: 'Par défaut, nom par nom', tuto: 'creneaux-jeunes' },
    ],
  },
  club: {
    label: 'Le club',
    lead: {
      bureau: 'Tout le club, rangé par ce que le club veut faire : faire jouer, mobiliser les bénévoles, faire vivre le club, le financer, gérer les adhérents, se montrer et recruter. Chaque rubrique a ses écrans et ses modules éteints (« Activer », avec leur pourquoi). Les réglages : la ⚙ de chaque module, ou « Modules et réglages ». Licences, agenda et bénévolat, activés, sont dans les onglets du bas.',
      partenaire: 'Le club que vous soutenez : son agenda, sa page publique, le don au club, son adresse de contact.',
      supporter: 'La vie du club (l’agenda, les annonces, les matchs du club, les partenaires), aider le club (inviter, trouver un partenaire), écrire au bureau ; en bas, la page publique.',
      default: 'Ton équipe d’abord (matchs et résultats, défis et gages, prépa), puis la vie du club, aider le club (bénévolat, inviter, trouver un partenaire) et le bureau.',
    },
    leadFor: x => (x.role === 'bureau' || x.role === 'partenaire' || x.role === 'technique' ? undefined : memberClubLead(x)),
    gestures: [
      { title: 'Équipes et coachs', sub: 'La saison, ses équipes, un coach par équipe', tour: 'saison-equipes', for: ['bureau'] },
      { title: 'Activer un module', sub: 'Bénévolat, licences, partenaires… : un assistant, puis « Activer »', tour: 'activer-module', for: ['bureau'] },
      { title: 'Les réglages du bénévolat', sub: 'Besoins des matchs, parcours, jeunes, groupes', tour: 'reglages-benevolat', for: ['bureau'] },
      { title: 'Mettre en ligne la page du club', sub: 'Matchs, résultats, contact', tour: 'page-publique', for: ['bureau'] },
      { title: 'Inviter un coach, donner un rôle', sub: 'Tous les rôles', tour: 'membres-roles', for: ['bureau'] },
      { title: 'Inviter quelqu’un', sub: 'Quelqu’un qui veut jouer, un bénévole', tour: 'inviter', for: ['joueur', 'parent', 'coach', 'supporter', 'membre'], adultOnly: true,
        when: inviteTile, subFor: x => cap(inviteWhat(x).join(', ')) },
      { title: 'Le bureau', sub: 'Fonctions, signatures, qui reçoit quoi', tour: 'le-bureau', for: ['bureau'] },
      { title: 'Signer un reçu fiscal', sub: 'Le vrai cerfa, ta signature', tour: 'signer-recu', for: ['bureau'] },
      { title: 'Le registre des reçus', sub: 'Numéros, déclaration annuelle des dons', tour: 'registre-recus', for: ['bureau'] },
      { title: 'Écrire au bureau', sub: 'Sans numéro à donner', tour: 'qui-contacter', for: ['joueur', 'parent', 'coach', 'supporter', 'membre'], adultOnly: true, when: x => x.adult },
    ],
  },
  'club/modules': {
    label: 'Modules et réglages',
    lead: { default: 'Les modules rangés par objectif du club. La roue d’un module activé ouvre ses réglages (la même page que sa ⚙). Un module éteint est caché aux membres et reste ici : « Configurer et activer » ouvre son assistant, qui l’active seulement à la fin ; un assistant arrêté se reprend là où il en était. Éteindre ne supprime rien.' },
    gestures: [
      { title: 'Activer un module', sub: 'Son assistant, puis « Activer »', tour: 'activer-module' },
      { title: 'L’assistant d’un module', sub: 'Configurer, reprendre, revoir', tour: 'configurer-module' },
      { title: 'Les réglages du bénévolat', sub: 'Besoins des matchs, parcours, jeunes, groupes', tour: 'reglages-benevolat', when: x => x.on('benevolat') },
    ],
  },
  'club/bureau': {
    label: 'Le bureau',
    lead: { default: 'Les fonctions du club (présidence, trésorerie, secrétariat et les tiennes) : qui les tient, qui signe les reçus fiscaux, qui reçoit quoi ; en tête, les messages reçus par tes fonctions.' },
    gestures: [{ title: 'Les fonctions du bureau', sub: 'Qui signe, qui reçoit quoi', tour: 'le-bureau' }],
  },
  'club/registre': {
    label: 'Reçus fiscaux',
    lead: { default: 'Le quotidien des reçus : les déclarations des bénévoles à valider, les reçus à signer, puis chaque reçu émis par le club, numéroté dans l’ordre, et la déclaration annuelle des dons. Les réglages : la ⚙, en haut à droite.' },
    gestures: [
      { title: 'Valider les reçus des bénévoles', sub: 'Les trajets, valider et signer', tour: 'recus-benevoles' },
      { title: 'Signer un reçu fiscal', sub: 'Le vrai cerfa, ta signature', tour: 'signer-recu' },
      { title: 'Le registre des reçus', sub: 'Numéros, déclaration annuelle', tour: 'registre-recus' },
    ],
  },
  'club/recus': {
    label: 'Réglages des reçus',
    lead: { default: 'Ce qui s’imprime sur chaque reçu fiscal (intérêt général, RNA ou SIREN, siège, objet), le barème kilométrique (celui des salariés, selon le véhicule de chaque bénévole), et les kilomètres des conducteurs de la saison. Les reçus à signer et à valider sont dans « Reçus fiscaux ».' },
    gestures: [{ title: 'Les reçus des bénévoles', sub: 'Valider, signer, le barème kilométrique', tour: 'recus-benevoles' }],
  },
  'club/equipes': {
    label: 'Équipes et coachs',
    lead: { default: 'La saison, ses équipes (filles, garçons, mixtes) reliées à la FFBB, et un coach par équipe ; le taux de « prévenu à temps » de chaque équipe (rien de nominatif).' },
    gestures: [{ title: 'Équipes et coachs', sub: 'La saison, un coach par équipe', tour: 'saison-equipes' }],
  },
  'club/page': {
    label: 'Page du club',
    lead: { default: 'La page du club : en ligne ou non, son adresse (« Voir la page ↗ », « Copier le lien »), ses abonnés et les demandes d’essai. Ses réglages (adresse, accroche, réseaux, entrée aux matchs, mentions légales) : la ⚙. Sans le module, il reste les mentions légales et données.' },
    gestures: [{ title: 'Mettre en ligne la page du club', sub: 'Matchs, résultats, contact', tour: 'page-publique' }],
  },
  // Le site du club (D-140).
  'club/modules/assistant': {
    label: 'L’assistant du module',
    lead: { default: 'Une question par écran : à quoi elle sert, un exemple pris dans le club, la réponse conseillée déjà cochée, « Je ne sais pas encore », et où ça se change ensuite. Une étape « sur place » montre l’écran du module (les plages, l’import FBI, tes partenaires…) : ce que tu y saisis est enregistré tout de suite. Rien ne change pour les membres avant le bouton du récapitulatif. « Arrêter et reprendre plus tard » garde les réponses pour le club.' },
    gestures: [{ title: 'L’assistant d’un module', sub: 'Configurer, reprendre, revoir', tour: 'configurer-module' }],
  },
  'club/site': {
    label: 'Site du club',
    lead: { default: 'Le site du club : en ligne ou non, ce qui attend « Publier », « Voir le site ». Le modifier au téléphone (textes, photos, ordre des blocs) ou sur un ordinateur (tout) ; les actualités, les pages, la boutique, l’historique ; l’option « Site personnalisable ».' },
    gestures: [
      { title: 'Le site du club', sub: 'Créer, changer de thème, modifier, publier', tour: 'site-creer' },
      { title: 'Le modifier au téléphone', sub: 'Textes, photos, ordre des blocs', tour: 'site-telephone' },
      { title: 'Écrire une actualité', sub: 'Publiée sur le site tout de suite', tour: 'site-actualite' },
      { title: 'Sur un ordinateur', sub: 'Blocs à glisser, options, pages, thème', tuto: 'site-ordinateur' },
    ],
  },
  'club/site/creer': {
    label: 'Créer mon site',
    lead: { default: 'L’assistant du site : le thème (aux couleurs et au logo du club, il se change à tout moment), puis les pages préparées, remplies avec l’app : prêt à 80 %. Gratuit tant qu’il n’est pas en ligne.' },
    gestures: [{ title: 'Créer le site du club', sub: 'Un thème, 8 pages préparées', tour: 'site-creer' }],
  },
  'club/site/editeur': {
    label: 'Éditeur du site',
    lead: { default: 'Le site tel que le verront les visiteurs. Au téléphone : textes, photos, ordre des blocs, publier ; sur un ordinateur : tout.' },
    gestures: [{ title: 'Le modifier au téléphone', sub: 'Textes, photos, ordre des blocs', tour: 'site-telephone' },
      { title: 'Sur un ordinateur', sub: 'Blocs à glisser, options, pages, thème', tuto: 'site-ordinateur' }],
  },
  'club/site/actualites': {
    label: 'Actualités du site',
    lead: { default: 'Les actualités écrites dans l’app, publiées sur le site tout de suite (le bloc Actualités et leur page) ; une annonce aux membres si tu veux.' },
    gestures: [{ title: 'Écrire une actualité', sub: 'Titre, photo, texte, équipes', tour: 'site-actualite' }],
  },
  'club/site/boutique': {
    label: 'Boutique et paiement',
    lead: { default: 'Tes liens de paiement (cotisation, HelloAsso, don) : le bloc Boutique du site les montre. Le paiement se fait chez l’organisme ; Arceau n’encaisse rien.' },
    gestures: [{ title: 'Le site du club', sub: 'Créer, modifier, publier', tour: 'site-creer' }],
  },
  'club/site/historique': {
    label: 'Historique du site',
    lead: { default: 'Chaque publication, avec qui et quoi. Revenir à une version en fait un brouillon : rien ne change en ligne avant « Publier ».' },
    gestures: [{ title: 'Revenir à une version', sub: 'Elle redevient un brouillon', tour: 'site-historique' }],
  },
  'club/site/pages': {
    label: 'Pages du site',
    lead: { default: 'Les pages du site dans l’ordre du menu, les pages d’équipe et les mentions légales générées. Ranger le menu, ajouter une page : sur ordinateur.' },
    gestures: [{ title: 'Le modifier au téléphone', sub: 'Textes, photos, ordre des blocs', tour: 'site-telephone' }],
  },
  'club/page/reglages': {
    label: 'Réglages de la page',
    lead: { default: 'Ce que montre la page du club : son adresse et sa mise en ligne, l’accroche, « Viens essayer », les réseaux sociaux, l’entrée aux matchs de chaque équipe, les mentions légales et données.' },
    gestures: [{ title: 'Mettre en ligne la page du club', sub: 'Matchs, résultats, contact', tour: 'page-publique' }],
  },
  'club/identite': {
    label: 'Nom, couleurs et logo',
    lead: { default: 'Le nom du club, ses couleurs et son logo : ils habillent l’app, la page du club et les liens partagés.' },
    gestures: [{ title: 'Le nom, les couleurs, le logo', sub: 'Tout le club voit l’app à ses couleurs', tour: 'identite-club' }],
  },
  'club/abonnement': {
    label: 'Abonnement Arceau',
    lead: { default: 'Le devis, l’accord et la facture de l’abonnement du club ; recommander Arceau à un autre club.' },
    gestures: [{ title: 'Le devis Arceau', sub: 'Recevoir, accepter', tour: 'abonnement' }],
  },
  'club/messages': {
    label: 'Messages reçus',
    lead: { default: 'Ce que les membres écrivent aux fonctions que tu tiens (dans « Qui contacter ») : réponds ici, ta réponse arrive dans leur app, sans numéro ni e-mail échangés.' },
    gestures: [],
  },
  'club/contacter': {
    label: 'Qui contacter',
    lead: { default: 'Les fonctions du bureau et pour quoi leur écrire : ton message arrive dans l’app de la personne, sans numéro à donner.' },
    gestures: [{ title: 'Écrire au bureau', sub: 'Sans numéro à donner', tour: 'qui-contacter' }],
  },
  moi: {
    label: 'Moi',
    lead: { partenaire: 'Vos badges, les notifications de ce téléphone, votre compte.', default: 'Ce qui est à toi : tes badges et tes avantages, tes suivis, tes trajets, tes indisponibilités, les notifications, ton compte. Inviter et trouver un partenaire sont dans Club › Aider le club.' },
    leadFor: meLead,
    gestures: [
      { title: 'Mes badges', sub: 'Parcours, grades, récompenses', tour: 'mes-badges', when: x => x.role !== 'abonne', vous: { title: 'Vos badges' } },
      { title: 'Une indisponibilité', sub: 'Blessure, vacances, examens', tour: 'indisponibilite', for: ['joueur', 'parent'] },
      { title: 'Activer les notifications', sub: 'Sur ce téléphone', tour: 'notifications' },
      // La ligne « Mes trajets pour le club » : un compte adulte du club, le bénévolat activé (recus-entries.ts).
      { title: 'Mes trajets pour le club', sub: 'Les km laissés au club : un reçu fiscal', tour: 'mes-trajets', adultOnly: true, when: x => x.adult && x.on('benevolat') },
      { title: 'Ma signature', sub: 'La voir telle qu’elle se pose sur un reçu, la changer', tour: 'ma-signature', for: ['bureau'] },
    ],
  },
  // D-149, D-154, D-157 : les skins (Moi › Apparence ; pas de visite guidée).
  'moi/skins': {
    label: 'Mes skins',
    lead: { default: 'L’allure de ton app : le skin porté, ceux qui sont presque à toi, toute la collection. Ils se gagnent en utilisant l’app, jamais en payant ; joueuses et joueurs : l’équipe voit seulement le cadre de ta photo.' },
    gestures: [
      { title: 'Porter un skin', sub: 'Toucher un skin gagné, « Porter ce skin »', tuto: 'mes-skins' },
      { title: 'Voir comment gagner un skin', sub: 'Ses conditions, où tu en es, « Essayer 10 secondes »', tuto: 'mes-skins' },
    ],
  },
  visibilite: {
    label: 'Visibilité',
    lead: { default: 'Ce que les membres voient de vous, et vos demandes au club : bannière d’accueil, « match présenté par ».' },
    gestures: [{ title: 'Demander de la visibilité', sub: 'Le bureau valide', tour: 'partenaire-visibilite' }],
  },
  publier: {
    label: 'Publier',
    lead: { default: 'Votre offre réservée aux membres, envoyée au club pour validation.' },
    gestures: [{ title: 'Publier une offre', sub: 'Réservée aux membres, le bureau valide', tuto: 'partenaire-visibilite' }],
  },
  matchs: {
    label: 'Matchs', team: true, teamInRoute: true,
    lead: {
      coach: 'Un seul écran pour les matchs : tout le club (à venir, résultats, « Je viens voir »), ou ton équipe avec le filtre : bilan par compétition, classement, la synchro FFBB. Touche un match pour sa fiche.',
      bureau: 'Les matchs de tout le club, à venir et résultats ; le filtre par équipe ouvre son bilan, son classement et ses compétitions FFBB.',
      abonne: 'Les matchs de toutes les équipes du club ; une étoile pour suivre une équipe.',
      default: 'Les matchs de tout le club : « Je viens voir », un mot pour encourager, une étoile pour suivre une équipe ; le filtre ouvre les matchs de ton équipe, avec le classement.',
    },
    leadFor: x => (['coach', 'bureau', 'abonne', 'technique'].includes(x.role) ? undefined
      : `Les matchs de tout le club : « Je viens voir », un mot pour encourager${x.on('page') ? ', une étoile pour suivre une équipe' : ''}${x.team && !x.vous ? ' ; le filtre ouvre les matchs de ton équipe, avec le classement' : ''}.`),
    gestures: [
      { title: 'Convoquer pour le match', sub: 'Composition, renforts', tour: 'convoquer-match', for: ['coach'] },
      { title: 'Suivre une équipe', sub: 'Ses résultats et ses changements', tour: 'suivre' },
      { title: 'Encourager une équipe', sub: 'Un mot lu avant le match', tour: 'encourager', for: ['coach', 'technique', 'bureau', 'joueur', 'parent', 'partenaire', 'supporter', 'membre'] },
    ],
  },
  'matchs-club': {
    label: 'Matchs du club',
    lead: { abonne: 'Les matchs de toutes les équipes du club ; une étoile pour suivre une équipe.',
      default: 'Les matchs de tout le club : « Je viens voir », un mot pour encourager l’équipe, et une étoile pour suivre une équipe.' },
    gestures: [
      { title: 'Suivre une équipe', sub: 'Ses résultats et ses changements', tour: 'suivre' },
      { title: 'Encourager une équipe', sub: 'Un mot lu avant le match', tour: 'encourager', for: ['coach', 'technique', 'bureau', 'joueur', 'parent', 'partenaire', 'supporter', 'membre'] },
    ],
  },
  suivis: {
    label: 'Mes suivis',
    lead: { default: 'Les équipes que tu suis, et leurs réglages : le résultat, les changements, la veille du match.' },
    leadFor: x => `Les équipes que ${x.vous ? 'vous suivez' : 'tu suis'}, et leurs réglages : le résultat, les changements, la veille du match.${x.parent ? ' Celles de tes enfants sont d’office.' : ''}`,
    gestures: [{ title: 'Suivre une équipe', sub: 'Ses résultats et ses changements', tour: 'suivre' }],
  },
  console: {
    label: 'Console',
    lead: { default: 'Les clubs, les inscriptions de la page de vente, la prospection : tout tourne seul. Ici, on regarde, on n’agit qu’en secours.' },
    gestures: [],
  },
  membres: {
    label: 'Membres et rôles',
    lead: { default: 'Tous les comptes du club, leurs rôles, et les invitations pour tous les rôles.' },
    gestures: [
      { title: 'Inviter, donner un rôle', sub: 'Par e-mail ou par un lien', tour: 'membres-roles' },
      { title: 'Inviter le reste du bureau', sub: 'Trésorerie, secrétariat', tour: 'pp-bureau' },
    ],
  },
  partenaires: {
    label: 'Partenaires',
    lead: { bureau: 'Les partenaires du club, leurs contrats, leur visibilité et leurs demandes.', default: 'Les partenaires du club et leurs offres pour les membres.' },
    minorLead: 'Les partenaires qui soutiennent le club.',
    gestures: [
      { title: 'Ajouter un partenaire', sub: 'Et lui ouvrir son espace', tour: 'partenaires', for: ['bureau'] },
      { title: 'Inviter un partenaire au match', sub: 'Un e-mail aux couleurs du club, sa réponse en un clic', tour: 'inviter-partenaire', for: ['bureau'] },
      { title: 'L’app offerte par un partenaire', sub: 'Proposée d’office, la promesse de don', tour: 'app-offerte', for: ['bureau'] },
      { title: 'Noter un don', sub: 'Et préparer son reçu fiscal', tour: 'noter-don', for: ['bureau'] },
      { title: 'Prospecter une entreprise', sub: 'Le segment Prospection : la liste, le suivi, le kit', tour: 'prospection', for: ['bureau'] },
    ],
  },
  'partenaires/reglages': {
    label: 'Réglages des partenaires',
    lead: { default: 'Ce qui se règle une fois : l’offre aux entreprises (les formules), le cadeau de l’apporteur, l’app offerte par un partenaire, et ce que le club offre à ses partenaires pour garder le lien.' },
    gestures: [{ title: 'Garder le lien', sub: 'Ce que le club offre, ce mois-ci, la saison', tour: 'garder-le-lien' }],
  },
  // Garder le lien avec les partenaires (D-125) : le bureau ; l'espace du partenaire (vouvoiement).
  lien: {
    label: 'Garder le lien',
    lead: {
      bureau: 'Un bon moment par trimestre pour chaque partenaire, sans jamais lui demander d’argent : ce que l’app propose ce mois-ci, la saison des attentions, ce que le club offre. Rien ne part sans toi.',
      partenaire: 'Les invitations du club à un match (« Je viens » ou « Je ne peux pas », aussi par e-mail), votre bon du soir du match à montrer à la buvette, les nouvelles du club ; « Seulement dans l’app » en bas.',
      default: 'Les attentions du club pour ses partenaires.',
    },
    gestures: [
      { title: 'Inviter un partenaire au match', sub: 'Ce que le club offre, qui l’accueille, l’e-mail du club', tour: 'inviter-partenaire', for: ['bureau'] },
      { title: 'Garder le lien', sub: 'Ce mois-ci, la saison, ce que le club offre', tour: 'garder-le-lien', for: ['bureau'] },
      { title: 'Répondre à une invitation', sub: 'Et utiliser votre bon le soir du match', tour: 'partenaire-invitation', for: ['partenaire'] },
    ],
  },
  subventions: {
    label: 'Subventions',
    lead: { default: 'Les dossiers de subvention du club : le calendrier des campagnes, les dossiers, la fiche action et le texte à coller. Le coffre des pièces et les chiffres du club (ta valeur fait foi) : la ⚙. Le club dépose lui-même ; le bureau seul voit tout ça.' },
    gestures: [
      { title: 'Préparer un dossier', sub: 'Campagne, pièces, fiche action, texte à coller', tour: 'subventions' },
      { title: 'Vérifier les chiffres', sub: 'Valider ou corriger ce qu’Arceau propose', tour: 'subventions-chiffres' },
      { title: 'Rendre le bilan', sub: 'Indicateurs réalisés, budget, CERFA 15059', tour: 'subventions-bilan' },
    ],
  },
  'subventions/reglages': {
    label: 'Réglages des subventions',
    lead: { default: 'Ce qui se dépose et se vérifie une fois par an, repris dans chaque dossier : les pièces du club (statuts, RIB, comptes…) et ses chiffres (ta valeur fait foi).' },
    gestures: [
      { title: 'Vérifier les chiffres', sub: 'Valider ou corriger ce qu’Arceau propose', tour: 'subventions-chiffres' },
      { title: 'Préparer un dossier', sub: 'Campagne, pièces, fiche action, texte à coller', tour: 'subventions' },
    ],
  },
  'avantages/reglages': {
    label: 'Réglages des avantages',
    lead: { default: 'Les avantages du club et les offres des partenaires : les créer, les limiter (« 1 fois par personne », « 5 en tout »), les remettre en jeu ; les validations de tout le club, avec le prénom. Les récompenses des grades se règlent avec le bénévolat : un renvoi y mène.' },
    gestures: [{ title: 'Un avantage en nombre limité', sub: '« 5 en tout », premiers arrivés, premiers servis', tour: 'avantages-bureau' },
      { title: 'Les validations', sub: 'Qui a validé, et comment', tour: 'valider-avantage-club' }],
  },
  // Le planning des salles (D-123, module de D-122 : src/ui/salles-module.ts).
  salles: {
    label: 'Les salles',
    lead: {
      bureau: 'La semaine du club sur un seul écran : en tête le bandeau des vacances et ce qui est à faire (un match hors de notre planning attribué, un lieu FFBB à relier, demandes de créneau, créneau perdu, séances hors créneau, réceptions), puis la grille salles × jours : chaque plage du club en cadre, les créneaux des équipes, le libre, les matchs, un match hors plage en orange, une dérogation en cours et ses créneaux demandés. Touche les heures d’une plage pour ses créneaux, le libre pour le donner, une case pour la déclarer perdue ; un jour, pour le voir heure par heure. Le planning attribué, les gestionnaires, la zone et les règles : la ⚙.',
      coach: 'Ta semaine des salles, en lecture : les plages du club en cadre, tes créneaux entourés, les créneaux libres à demander, ceux libérés cette semaine à prendre ; « À décider » quand une salle est prise ou que tes créneaux de vacances sont remplis.',
      technique: 'La semaine des salles : qui occupe quel créneau, les créneaux libres et libérés.',
      supporter: 'Si tu es responsable des salles : la semaine du club, ce qui est à faire, les vacances en 3 étapes, les créneaux perdus ou en plus ; les gestionnaires, les salles et les règles à la ⚙. Sinon : les salles de réception du club.',
      default: 'Les salles de réception du club : demande-les au bureau pour un pot, une réunion, un anniversaire.',
    },
    gestures: [
      { title: 'Les salles', sub: 'La semaine, l’à-faire, une case touchée', tour: 'nos-creneaux', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Notre planning attribué', sub: 'Les plages, puis les créneaux des équipes', tour: 'planning-attribue', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Les matchs dans les plages', sub: 'Hors plage, lieu FFBB à relier, dérogation', tour: 'matchs-plages', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Les vacances en 3 étapes', sub: 'Le bandeau, les salles, les coachs, publier', tour: 'vacances-3-etapes', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Étape 1 : les salles', sub: 'Ouverte, fermée, horaires à part ; les fériés', tour: 'vacances-salles', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Arbitrer et publier', sub: 'Seulement les ajouts et les chevauchements', tour: 'arbitrer-salles', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Créneau perdu, créneau en plus', sub: 'Depuis la case touchée ; rien n’est annulé d’office', tour: 'creneau-perdu', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Mise en cohérence', sub: 'Les séances qui ne collent à aucun créneau', tour: 'mise-en-coherence', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Les gestionnaires', sub: 'Contact, comment leur demander', tour: 'gestionnaires-salles', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Les dates des vacances', sub: 'L’académie, le calendrier officiel', tuto: 'zone-vacances', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Demander un créneau libre', sub: 'Toutes les semaines ou une fois', tour: 'creneau-libre', for: ['coach'] },
      { title: 'Mes créneaux de vacances', sub: 'Déjà remplis : garder, retirer, ajouter', tour: 'souhaits-salles', for: ['coach'] },
      { title: 'Ma séance n’a plus de salle', sub: 'Changer de salle, annuler, garder', tuto: 'seance-sans-salle', for: ['coach'] },
      { title: 'Le planning d’une salle', sub: 'Créneaux, chevauchements, créneaux libérés', tour: 'planning-salle', for: ['bureau', 'coach', 'technique'] },
      { title: 'Demander le club-house', sub: 'Un pot, une réunion, un anniversaire', tour: 'demander-salle', adultOnly: true, for: ['coach', 'joueur', 'parent', 'supporter'] },
      { title: 'Ajouter une salle', sub: 'Son gestionnaire, fusionner deux salles', tuto: 'salles-club', for: ['bureau'] },
      { title: 'Répondre à une demande', sub: 'Accepter, une autre heure, le gestionnaire', tuto: 'repondre-salle', for: ['bureau'] },
    ],
  },
  'salles/salles': {
    label: 'Les salles',
    lead: {
      bureau: 'Les salles du club, chacune avec son gestionnaire : gymnases, autres salles, salles de réception. Touche une salle pour son planning ; « ＋ Ajouter une salle ».',
      supporter: 'Les salles du club et leurs demandes de réception : accepte, propose une autre heure, ou prépare la demande au gestionnaire.',
      default: 'Les salles de réception du club : demande-les au bureau pour un pot, une réunion, un anniversaire.',
    },
    gestures: [
      { title: 'Répondre à une demande', sub: 'Accepter, une autre heure, le gestionnaire', tuto: 'repondre-salle', for: ['bureau', 'supporter'] },
      { title: 'Ajouter une salle', sub: 'Son gestionnaire, fusionner deux salles', tuto: 'salles-club', for: ['bureau'] },
      { title: 'Demander le club-house', sub: 'Un pot, une réunion, un anniversaire', tour: 'demander-salle', adultOnly: true, for: ['coach', 'joueur', 'parent', 'supporter'] },
    ],
  },
  'salles/salle': {
    label: 'Planning de la salle',
    lead: {
      coach: 'La semaine de la salle : les séances et les matchs, les créneaux du club, ceux qu’une séance annulée libère (tu peux les prendre), les chevauchements.',
      default: 'La semaine de la salle : les séances et les matchs, les créneaux du club donnés par son gestionnaire, les créneaux libérés, les chevauchements.',
    },
    gestures: [
      { title: 'Le planning d’une salle', sub: 'Créneaux, chevauchements, créneaux libérés', tour: 'planning-salle' },
      { title: 'Noter les créneaux du gestionnaire', sub: 'Une fois par saison, l’équipe de chaque créneau', tuto: 'salles-club', for: ['bureau'] },
    ],
  },
  'salles/reglages': {
    label: 'Réglages des salles',
    lead: { default: 'Ce qui se règle une fois par saison, en 4 rubriques : les salles et notre planning attribué (gestionnaire › salle › plage, les créneaux des équipes dedans, les lieux FFBB à relier, les salles de réception), les gestionnaires (contact, comment leur demander, chacun en feuille), la zone et les vacances (l’académie, la source des dates, leur dernière lecture), les règles (le responsable des salles, le bandeau des vacances, la date limite des coachs).' },
    gestures: [
      { title: 'Notre planning attribué', sub: 'Les plages, puis les créneaux des équipes', tour: 'planning-attribue' },
      { title: 'Les gestionnaires', sub: 'Contact, comment leur demander', tour: 'gestionnaires-salles' },
      { title: 'Les dates des vacances', sub: 'L’académie, le calendrier officiel', tuto: 'zone-vacances' },
      { title: 'Ajouter une salle', sub: 'Son gestionnaire, fusionner deux salles', tuto: 'salles-club' },
    ],
  },
  'salles/jour': {
    label: 'La journée heure par heure',
    lead: { coach: 'Une journée heure par heure, une colonne par salle : tes créneaux entourés, les créneaux libres à demander.',
      default: 'Le zoom d’une journée : une colonne par salle, heure par heure. Touche un créneau pour changer son équipe, un créneau libre pour le donner.' },
    gestures: [{ title: 'Les salles', sub: 'La semaine, l’à-faire, une case touchée', tour: 'nos-creneaux', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Demander un créneau libre', sub: 'Toutes les semaines ou une fois', tour: 'creneau-libre', for: ['coach'] }],
  },
  'salles/coherence': {
    label: 'Mise en cohérence',
    lead: { default: 'Les séances à venir qui ne collent à aucun créneau du club : « Rattacher » au créneau de l’équipe, « Marquer « Ailleurs » » (hors planning des salles), ou « Demander au coach ». Rien n’est déplacé d’office.' },
    gestures: [
      { title: 'Mise en cohérence', sub: 'Rattacher, Ailleurs, demander au coach', tour: 'mise-en-coherence', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Les salles', sub: 'La semaine, l’à-faire, une case touchée', tour: 'nos-creneaux', for: ['bureau', 'supporter'], adultOnly: true },
    ],
  },
  'salles/perdu': {
    label: 'Créneau perdu',
    lead: {
      coach: 'Un créneau perdu touche ta séance : rien n’est annulé d’office. Change de salle (les créneaux libres sont proposés) ou annule ; sans choix, le responsable des salles est prévenu la veille.',
      default: 'Le créneau perdu et ce qu’il touche : rien n’est annulé d’office. Un match à domicile : la dérogation est préparée pour le bureau. Un entraînement : son coach change de salle ou annule.',
    },
    gestures: [
      { title: 'Créneau perdu, créneau en plus', sub: 'Rien n’est annulé d’office', tour: 'creneau-perdu', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Ma séance n’a plus de salle', sub: 'Changer de salle, annuler, garder', tuto: 'seance-sans-salle', for: ['coach'] },
    ],
  },
  'salles/vacances': {
    label: 'Les vacances',
    lead: {
      coach: 'Tes créneaux de vacances : tes créneaux habituels sont déjà remplis dans les salles ouvertes. Garde, retire, ajoute un moment, confirme ; sans réponse, ils sont gardés.',
      default: 'Les vacances en 3 étapes : (1) chaque salle ouverte, fermée ou à horaires à part, les jours fériés fermés ; (2) les coachs confirment leurs créneaux habituels déjà remplis ; (3) tu n’arbitres que les ajouts et les chevauchements, puis tu publies.',
    },
    gestures: [
      { title: 'Les vacances en 3 étapes', sub: 'Le bandeau, les salles, les coachs, publier', tour: 'vacances-3-etapes', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Étape 1 : les salles', sub: 'Ouverte, fermée, horaires à part ; les fériés', tour: 'vacances-salles', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Arbitrer et publier', sub: 'Seulement les ajouts et les chevauchements', tour: 'arbitrer-salles', for: ['bureau', 'supporter'], adultOnly: true },
      { title: 'Mes créneaux de vacances', sub: 'Déjà remplis : garder, retirer, ajouter', tour: 'souhaits-salles', for: ['coach'] },
    ],
  },
  'salles/arbitrer': {
    label: 'Arbitrer les souhaits',
    lead: { default: 'Jour par jour, salle par salle : accepter, déplacer (créneaux libres proposés, un mot), refuser ; les chevauchements en rouge. La publication attend une réponse à chaque souhait.' },
    gestures: [{ title: 'Arbitrer et publier', sub: 'Seulement les ajouts et les chevauchements', tour: 'arbitrer-salles', for: ['bureau', 'supporter'], adultOnly: true }],
  },
  'salles/saison': {
    label: 'Le planning de la saison',
    lead: {
      coach: 'Le planning de la saison suivante : pars de tes créneaux de cette saison, ajoute ou retire tes souhaits.',
      default: 'Le planning de la saison suivante : chaque coach part de ses créneaux, le responsable des salles arbitre et publie.',
    },
    gestures: [{ title: 'Mes souhaits', sub: 'Les jours, la journée ou des heures', tour: 'souhaits-salles', for: ['coach'] }],
  },
  prospection: {
    label: 'Prospection',
    lead: { default: 'Les entreprises autour du gymnase, d’après le registre public et classées par affinité ; le suivi de chacune (étape, responsable, prochaine action et rappel) ; le kit pour aller les voir. Le club contacte lui-même : Arceau n’écrit jamais aux entreprises.' },
    gestures: [
      { title: 'Prospecter une entreprise', sub: 'La liste, le suivi, « Qui connaît ? », le kit', tour: 'prospection' },
      { title: 'Le barème de l’affinité', sub: 'Secteur, taille, distance', tuto: 'prospection' },
    ],
  },
  annonces: {
    label: 'Annonces',
    lead: { default: 'Les messages et les sondages du club et de tes équipes.' },
    vous: 'Les messages et les sondages du club.',
    gestures: [
      { title: 'Lire, répondre à un sondage', sub: '« Compris », voter', tour: 'annonces' },
      { title: 'Diffuser un message', sub: 'À qui tu veux, avec le suivi de lecture', tour: 'diffuser', for: ['coach', 'bureau'] },
    ],
  },
  // D-155 : « Tout voir » de l'accueil, les mêmes lignes que son « À faire ».
  'a-faire': {
    label: 'À faire',
    lead: { default: 'Tout ce qui attend un geste de toi, une décision d’abord, puis une information. Chaque ligne se fait d’ici et disparaît quand c’est fait.' },
    vous: 'Ce que le club vous demande : chaque ligne se fait d’ici et disparaît quand c’est fait.',
    gestures: [],
  },
  notifs: {
    label: 'Notifications',
    lead: { default: 'Ce qui t’a été envoyé ; et, en haut, les notifications de ce téléphone.' },
    vous: 'Ce qui vous a été envoyé ; et, en haut, les notifications de ce téléphone.',
    gestures: [{ title: 'Activer les notifications', sub: 'Sur ce téléphone', tour: 'notifications' }],
  },
  essais: {
    label: 'Essais et demandes',
    lead: { default: 'Les demandes d’essai : page publique, liens d’équipe, amis invités. Dans l’effectif, essai d’abord, ou refuser.' },
    gestures: [{ title: 'Traiter une demande', sub: 'Dans l’effectif, essai d’abord, refuser', tour: 'demandes' }],
  },
  indispos: {
    label: 'Indisponibilités',
    lead: { default: 'Blessure, vacances, examens, ou chaque semaine (un cours, le travail) : l’absence est notée d’office aux séances concernées.' },
    gestures: [
      { title: 'Ajouter une indisponibilité', sub: 'Les dates, la case médicale si besoin', tour: 'indisponibilite' },
      { title: 'Une indisponibilité chaque semaine', sub: 'Des jours, une heure, jusqu’à une date', tour: 'indispo-semaine' },
    ],
  },
  // --- Les écrans hors des onglets (D-101) : chacun a aussi son « ? ». ---------------------------
  e: {
    label: 'Séance ou match', team: true,
    lead: {
      coach: 'La fiche de la séance ou du match : qui vient, l’appel, le plan ; pour un match, la composition, les renforts, le covoiturage, le direct et le score.',
      bureau: 'La fiche de la séance ou du match : qui vient ; pour un match, la convocation, le direct, le score, la feuille de match.',
      parent: 'L’heure, le lieu, la réponse pour ton enfant (on compte sur lui par défaut) ; pour un match : la convocation, le covoiturage, le direct, le score.',
      default: 'L’heure, le lieu, ta présence (on compte sur toi par défaut) ; pour un match : la convocation, le covoiturage, le direct, le score.',
    },
    vous: 'Le match du club : l’heure, le lieu, le direct et le score ; « Je viens voir » et un mot pour encourager l’équipe.',
    gestures: [
      { title: 'Faire l’appel', sub: 'Présences, absences, retards', tour: 'faire-appel', for: ['coach'] },
      { title: 'Convoquer pour le match', sub: 'Composition, renforts', tour: 'convoquer-match', for: ['coach'] },
      { title: 'Annuler une séance', sub: 'L’équipe est prévenue tout de suite', tour: 'annuler-seance', for: ['coach'] },
      { title: 'Saisir les stats en direct', sub: 'Action par action, ou le score seul', tuto: 'stats-direct', for: ['coach'] },
      { title: 'Valider un changement détecté sur FFBB', sub: 'Le coach ; le bureau aussi à domicile', tour: 'match-ffbb', for: ['coach', 'bureau'] },
      { title: 'Score FFBB différent', sub: 'Garder le nôtre, prendre celui de la FFBB ou corriger', tour: 'score-ffbb', for: ['coach', 'bureau'] },
      { title: 'Prévenir d’une absence', sub: 'Le coach est prévenu', tour: 'prevenir-absence', for: ['joueur', 'parent'] },
      { title: 'Après la limite', sub: 'La raison d’un toucher, un mot', tuto: 'desistement-tardif', for: ['joueur', 'parent'] },
      { title: 'Prévenu tard, sans prévenir', sub: 'Ne pas compter, ne pas lui écrire', tuto: 'desistements-equipe', for: ['coach'] },
      { title: 'Répondre Incertaine', sub: 'Le coach compte à part, une relance', tour: 'incertaine', for: ['joueur', 'parent'] },
      { title: 'Proposer sa voiture', sub: 'Pour un match à l’extérieur', tuto: 'covoiturage', for: ['joueur', 'parent', 'coach'], adultOnly: true },
      { title: 'Proposer ta place libre', sub: 'Aux familles qui l’autorisent', tuto: 'covoit-accords', for: ['joueur', 'parent', 'coach'], adultOnly: true },
      { title: 'Publier un selfie de victoire', sub: 'Le message des accords, la case', tuto: 'selfie-accords', for: ['coach', 'joueur', 'parent'], adultOnly: true },
    ],
  },
  seance: {
    label: 'Plan de séance', team: true,
    lead: {
      coach: 'Le segment « Le plan » de la séance : les exercices, leurs durées et les horaires calculés ; « Envoyer à l’équipe » quand c’est prêt. Tes notes et le plan B restent pour toi. « La séance » : qui vient, l’appel.',
      default: 'Le programme de la séance envoyé par le coach : les exercices et leurs horaires.',
    },
    gestures: [
      { title: 'Préparer le plan de séance', sub: 'Exercices du club, durées', tour: 'plan-seance', for: ['coach'] },
      { title: 'Tirer les équipes au sort', sub: 'Niveau, postes, liens ; puis la TV', tour: 'tirage-equipes', for: ['coach'] },
    ],
  },
  play: {
    label: 'Un play', team: true,
    lead: {
      coach: 'Le play animé temps par temps, en 2D ou en 3D, et ses variantes ; tu choisis s’il reste aux coachs ou s’il est montré à l’équipe.',
      default: 'Le play animé temps par temps, en 2D ou en 3D, et ses variantes. ▶ lance l’animation.',
    },
    gestures: [
      { title: 'Voir un play', sub: 'En 2D, en 3D, ses variantes', tour: 'voir-play' },
      { title: 'Créer un play', sub: 'Dessiner, animer, montrer à l’équipe', tour: 'creer-play', for: ['coach'] },
    ],
  },
  stats: {
    label: 'Stats en direct',
    lead: { default: 'Le score et les stats du match, action par action et quart-temps par quart-temps ; « Score seul » pour aller vite ; « Table de marque » pour un amical : fautes, temps-morts, chrono, sur un seul téléphone. Tout le club suit le direct.' },
    gestures: [{ title: 'Saisir les stats en direct', sub: 'Lancer le direct, puis chaque action', tuto: 'stats-direct' },
      { title: 'La table de marque', sub: 'Un amical, un tournoi : un seul téléphone tient la table', tuto: 'table-de-marque' }],
  },
  // D-175, D-177 : le tournoi du club (ses écrans de préparation, le jour J, « Tables ») et ses réglages.
  tournoi: {
    label: 'Le tournoi du club',
    lead: { default: 'Le tournoi du club en trois phases : l’annonce (quoi, quand, où, pour qui ; les règles ; l’affiche ; les inscriptions ; les invitations ; bénévoles et partenaires), puis « Annoncer le tournoi » ; la préparation (équipes, niveaux, poules, phases finales, planning), puis « Publier le planning » ; le jour J, ce qui demande un geste et le fil du jour jusqu’à « Clore ». Hors compétition FFBB.' },
    gestures: [
      { title: 'Préparer un tournoi', sub: 'Quatre questions, le format proposé, la fin prévue', tour: 'tournoi-preparer' },
      { title: 'Annoncer un tournoi', sub: 'Trois phases, les invitations, « Annoncer », « Publier le planning »', tour: 'tournoi-annoncer' },
      { title: 'Les équipes, les poules et le planning', sub: 'Inscrire une équipe, poules équilibrées, publier', tour: 'tournoi-poules' },
      { title: 'Le jour J', sub: 'À vérifier, retards, tables, QR, palmarès', tour: 'tournoi-jour-j' },
    ],
  },
  'tournoi/reglages': {
    label: 'Réglages des tournois',
    lead: { default: 'Les organisateurs nommés par le bureau, le tournoi interne des coachs, le modèle de cartes règles du club.' },
    gestures: [{ title: 'Préparer un tournoi', sub: 'Du tableur au tournoi publié', tuto: 'tournoi-preparer' }],
  },
  table: {
    label: 'Table de marque',
    lead: { default: 'La table de marque d’entraînement, en 3x3 ou en 5x5 : le score, les fautes, les temps-morts et le chrono, sur ce téléphone. Rien n’est enregistré ni public. Hors compétition FFBB.' },
    gestures: [{ title: 'La table de marque', sub: 'Table simple ou marqueur complet', tuto: 'table-de-marque' }],
  },
  repartir: {
    label: 'Deux matchs le même jour', team: true,
    lead: { default: 'Deux équipes engagées du groupe jouent le même jour : qui joue où, dans la limite de la feuille de match de chacune. Les deux convocations suivent.' },
    gestures: [{ title: 'Répartir l’effectif', sub: 'Deux matchs, une seule liste', tuto: 'double-match' }],
  },
  defis: {
    label: 'Défis et gages', team: true,
    lead: {
      coach: 'Les défis de l’équipe (chacun pour soi ou tous ensemble ; un chiffre, des séries, le chrono ou le compte à rebours ; un podium), les défis entre catégories, et les gages tirés au sort.',
      default: 'Les défis de ton équipe, ta progression et leur podium ; les gages à tirer au sort, et tes idées de gages.',
    },
    gestures: [{ title: 'Les défis et les gages', sub: 'Lancer, saisir, tirer un gage', tuto: 'defis-gages' }],
  },
  marrainage: {
    label: 'Parrainage des jeunes',
    lead: {
      bureau: 'Les binômes du club : un plus grand parraine un plus jeune, une équipe engagée ou toute une équipe. Former un binôme, le resserrer, le terminer.',
      default: 'Un plus grand parraine un plus jeune, une équipe engagée ou toute une équipe : son prochain match (« J’y serai »), ses nouvelles, un encouragement.',
    },
    gestures: [{ title: 'Le parrainage des jeunes', sub: 'Les binômes, les encouragements', tuto: 'parrainage-jeunes' }],
  },
  pistes: {
    label: 'Trouver un partenaire',
    lead: {
      partenaire: 'Les entreprises que vous avez proposées au club, et où elles en sont. « Proposer une entreprise » pour en ajouter une.',
      default: 'Les entreprises que tu as proposées au club, et où elles en sont. « Proposer une entreprise » pour en ajouter une.',
    },
    vous: 'Les entreprises que vous avez proposées au club, et où elles en sont. « Proposer une entreprise » pour en ajouter une.',
    gestures: [{ title: 'Proposer une entreprise', sub: 'Le message tout prêt, ou son contact au bureau', tuto: 'trouver-partenaire' }],
  },
  feuilles: {
    label: 'Feuilles de match',
    lead: { default: 'Les matchs joués du club, toutes équipes : déposer la feuille de stats FFBB (PDF). Le coach est prévenu et décide si l’équipe la voit.' },
    gestures: [],
  },
  derogations: {
    label: 'Dérogations',
    lead: { default: 'Nos demandes et les demandes reçues, en deux onglets ; des puces par état (« À faire maintenant » d’abord), la recherche, les week-ends. Chaque dérogation en 3 étapes (les prévenir) ou 4 (demander leur accord), jusqu’à la confirmation sur FFBB ; rangée le lendemain du match. Les changements FFBB sans dérogation restent à valider, et les demandes des coachs se reprennent ici.' },
    gestures: [
      { title: 'Demander une dérogation', sub: 'L’autre club prévenu, puis FBI sans attendre', tour: 'derogation' },
      { title: 'Prévenir d’un petit changement', sub: 'La salle ou 1 h au plus : pas d’accord à attendre', tour: 'derogation-prevenir' },
      { title: 'Enregistrer une demande reçue', sub: 'Reliée au match, l’avis du coach', tour: 'derogation-recue' },
    ],
  },
  'derogations/fiche': {
    label: 'Une dérogation',
    lead: { default: 'Les étapes datées (3 pour « Les prévenir », 4 pour une demande d’accord) : qui, quand, par quel canal, le lien ouvert, la réponse ; le texte à reporter dans FBI. « Saisi sur FBI » est là tout de suite : la saisie n’attend pas la réponse.' },
    gestures: [{ title: 'Saisi sur FBI', sub: 'Le créneau saisi, sans attendre la réponse', tour: 'derogation' },
      { title: 'Ça leur pose problème', sub: 'Demander leur accord, ou saisir quand même', tour: 'derogation-prevenir' }],
  },
  // D-143 : ressentis, bilan de saison, assistant « Nouvelle saison » (socle).
  ressentis: {
    label: 'Ressentis',
    lead: {
      bureau: 'Les ressentis de chaque équipe, en moyennes : 8 semaines, la tendance, le taux de réponse. Une moyenne seulement à partir de 5 répondants ; jamais une réponse, un mot ou un prénom.',
      coach: 'Ce que ton équipe a vécu : le dernier match ou la dernière séance (la moyenne, qui est à regarder, les mots), la semaine (les 8 dernières, chacun en 5 points) et la saison. Rien ne sort du staff.',
      technique: 'Les réponses d’une équipe se lisent par son staff ; le bureau voit des moyennes par équipe.',
      parent: 'Les ressentis de tes enfants sans compte : après chaque match, et après les séances choisies par le coach, tu réponds pour eux d’un toucher. Un enfant qui a son compte répond lui-même, et sa réponse reste entre lui et le staff.',
      default: 'Tes ressentis : après chaque match, et après les séances que le coach a choisies, un toucher pour dire comment c’était. Seul le staff de ton équipe lit ta réponse.',
    },
    gestures: [
      { title: 'Donner son ressenti', sub: 'Un toucher, puis un mot si tu veux', tour: 'donner-ressenti', for: ['joueur', 'parent'] },
      { title: 'Lire les ressentis de l’équipe', sub: 'Le match, la semaine, la saison', tour: 'lire-ressentis', for: ['coach'] },
      { title: 'Les tendances des équipes', sub: 'Des moyennes, jamais une réponse', tour: 'ressentis-equipes', for: ['bureau'] },
    ],
  },
  'ressentis/reglages': {
    label: 'Réglages des ressentis',
    lead: { default: 'Après quelles séances le ressenti est demandé (toutes, la dernière de la semaine, jamais) ; après chaque match, toujours. Qui voit quoi, le rappel du lendemain à 10 h.' },
    gestures: [],
  },
  bilan: {
    label: 'Bilan de saison',
    lead: { default: 'Ton bilan de fin de saison : ton projet pour la saison prochaine, un coup de main, ta saison (ambiance, encadrement, créneaux), un mot. Chaque bloc dit qui le voit ; pour un mineur, c’est le parent qui répond.' },
    gestures: [{ title: 'Faire le bilan', sub: '2 minutes, modifiable jusqu’à la clôture', tour: 'faire-bilan' }],
  },
  'bilan/suivi': {
    label: 'Bilan de saison',
    lead: { default: 'La prévision des réinscriptions par équipe, ceux qui hésitent, arrêtent ou n’ont pas répondu (relancer, partager sur WhatsApp), les futurs officiels et les mots. La saison de chacun n’est jamais montrée : des moyennes, à partir de 5 réponses.' },
    gestures: [{ title: 'Suivre le bilan', sub: 'Prévision, personnes, mots', tour: 'suivre-bilan' }],
  },
  'bilan/reglages': {
    label: 'Réglages du bilan',
    lead: { default: 'Quand le bilan part (17 mai par défaut), les relances (31 mai, 7 juin) et la clôture (15 juin) ; ce qu’il demande et qui voit quoi.' },
    gestures: [],
  },
  'nouvelle-saison': {
    label: 'Nouvelle saison',
    lead: { default: 'L’assistant de juin, en 4 étapes : d’où on part, les équipes proposées (âges, projets du bilan), les relances et les dossiers de licence, le récapitulatif. Le 1er juillet, la saison préparée se met en place toute seule ; rien n’est effacé.' },
    gestures: [{ title: 'Préparer la saison prochaine', sub: 'Les équipes, les relances, les dossiers', tour: 'nouvelle-saison' }],
  },
  'derogations/reglages': {
    label: 'Réglages des dérogations',
    lead: { default: 'Au bout de combien de jours sans réponse l’étape passe en « sans réponse » (de 2 à 7). Un rappel, jamais un verrou : la saisie sur FBI n’attend pas.' },
    gestures: [],
  },
  // D-164 : « Mains vives », le premier jeu caméra (bêta).
  'mains-vives': {
    label: 'Mains vives',
    lead: { default: 'Avant la séance : où poser le téléphone, ce que la caméra va demander, ce qui reste sur le téléphone. La vidéo et l’image ne le quittent jamais ; Arceau garde seulement trois chiffres. Pour un défi Mains vives : son niveau, 60 s, puis « Envoyer mon score au défi ».',
      parent: 'La séance de ton enfant mineur passe par ton téléphone : où le poser, ce que la caméra va demander, ce qui reste sur le téléphone.' },
    minorLead: 'La séance passe par le téléphone d’un parent : la caméra ne s’ouvre pas sur ton compte. Ta progression arrive ici.',
    gestures: [{ title: 'Ma séance', sub: 'Poser le téléphone, le calage, 60 s', tour: 'mains-vives', for: ['joueur'] },
      { title: 'La séance de mon enfant', sub: 'Elle passe par ton téléphone', tour: 'mains-vives-enfant', for: ['parent'] }],
  },
  'mains-vives/progression': {
    label: 'Ma progression · Mains vives',
    lead: { default: 'Tes séances, tes records, les niveaux à débloquer : rien que toi, pas de classement. Le coach voit seulement que tu l’as fait, et combien de fois.',
      parent: 'Les séances de ton enfant, ses records, ses niveaux : pas de classement. Le coach voit seulement qu’il l’a fait.' },
    gestures: [{ title: 'Une séance', sub: 'Poser le téléphone, le calage, 60 s', tour: 'mains-vives', for: ['joueur'] }],
  },
  'mains-vives/enfant': {
    label: 'Mains vives · mon enfant',
    lead: { default: 'Ton enfant est mineur : la caméra s’ouvre sur ton téléphone, la vidéo y reste, et ses chiffres vont dans sa progression.' },
    gestures: [{ title: 'Lancer sa séance', sub: 'Sur ton téléphone, posé', tour: 'mains-vives-enfant' }],
  },
  'mains-vives/equipe': {
    label: 'Mains vives · proposer', team: true,
    lead: { default: 'Propose le jeu caméra à ton équipe, à faire chez soi : combien de fois, avant quand, le niveau. Tu verras qui l’a fait et combien de fois ; jamais la vidéo, ni leurs chiffres.' },
    gestures: [{ title: 'Proposer à l’équipe', sub: 'Les joueurs et les parents des jeunes sont prévenus', tour: 'mains-vives-proposer' }],
  },
  // D-141 : la formation des officiels (bêta).
  formation: {
    label: 'Formation',
    lead: { bureau: 'Les parcours du club pour devenir officiel ; « Nos officiels » face aux matchs à domicile du mois.',
      default: 'Les parcours du club pour devenir officiel : OTM ★ pour la table de marque, Arbitre club ★ pour les matchs de jeunes. Une certification du club, validée par un référent.' },
    gestures: [
      { title: 'Suivre mon parcours', sub: 'Documents, simulateurs, quiz, vrais matchs', tour: 'formation-eleve' },
      { title: 'Valider un badge', sub: 'Le dossier complet, un mot', tour: 'formation-referent', for: ['bureau', 'coach', 'supporter', 'joueur'], adultOnly: true },
      { title: 'Nos officiels', sub: 'Face aux matchs du mois', tour: 'formation-bureau', for: ['bureau'] },
    ],
  },
  'formation/parcours': {
    label: 'Un parcours',
    lead: { default: 'Chaque étape datée : le document, les simulateurs (ton meilleur essai face au seuil ; OTM : l’annonce écrite, puis l’arbitre et sa voix, puis comme en match ; chaque essai tire de nouvelles situations), le quiz, les vrais matchs ; puis la validation par un référent et le badge. En bas, « S’entraîner librement », sans note.' },
    minorLead: 'Chaque étape datée : le document, les simulateurs, les vrais matchs (ceux ouverts aux jeunes) ; tes scores ne sont vus que par toi, ton référent et tes parents. En bas, « S’entraîner librement », sans note.',
    gestures: [{ title: 'Suivre mon parcours', sub: 'Lancer, activer le son, puis le score revient tout seul', tour: 'formation-eleve' }],
  },
  'formation/valider': {
    label: 'À valider',
    lead: { default: 'Les dossiers complets de tes parcours, ceux en cours, ceux que tu as validés. « Valider » donne le badge jusqu’au 30 juin ; « Pas encore » envoie ton mot.' },
    gestures: [{ title: 'Valider un badge', sub: 'Le dossier complet, un mot', tour: 'formation-referent' }],
  },
  'formation/officiels': {
    label: 'Nos officiels',
    lead: { default: 'Les certifiés, ceux en formation, ceux à renouveler, face aux places à la table et aux arbitres des matchs à domicile du mois. La liste se copie pour le comité.' },
    gestures: [{ title: 'Nos officiels', sub: 'Proposer la table, copier la liste', tour: 'formation-bureau' }],
  },
  'formation/reglages': {
    label: 'Réglages de la formation',
    lead: { default: 'Les référents de chaque parcours (certifiés d’office), les supports du club, les seuils des simulateurs, le quiz, la pratique, la validité, les jeunes.' },
    gestures: [],
  },
  evaluation: {
    label: 'Évaluation', team: true,
    lead: { default: 'La campagne d’évaluation du responsable technique : chaque joueur, chaque critère, de 1 Découverte à 4 Maîtrisé. Un toucher, c’est enregistré.' },
    gestures: [],
  },
  composition: {
    label: 'Composition',
    lead: { default: 'Le brouillon de composition des équipes : qui va où. Ton avis, puis le responsable technique valide.' },
    gestures: [],
  },
  aide: {
    label: 'Aide',
    lead: {
      partenaire: 'Les tutos de vos rôles, la recherche, et le support : une question, un bug, une idée.',
      default: 'Les tutos de tes casquettes, la recherche, et le support : une question, un bug, une idée.',
    },
    vous: 'Les tutos de vos rôles, la recherche, et le support : une question, un bug, une idée.',
    gestures: [{ title: 'Écrire au support', sub: 'Bug, idée ou question', tour: 'ecrire-support' }],
  },
  support: {
    label: 'Boîte du support',
    lead: { default: 'Équipe Arceau : les messages de tous les clubs, leur état et la réponse, lue dans l’app.' },
    gestures: [],
  },
  prepa: {
    label: 'Prépa physique',
    lead: { coach: 'Les programmes de prépa : trois niveaux, le suivi de qui a fait quoi.', technique: 'Les programmes de prépa des équipes : trois niveaux, qui a fait quoi.',
      bureau: 'Les programmes de prépa des équipes : trois niveaux, qui a fait quoi.', parent: 'La séance du jour de ton enfant, son niveau, ses points.', default: 'Ta séance du jour, ton niveau, tes points.' },
    gestures: [
      { title: 'Valider ma séance', sub: 'Les points tout de suite', tour: 'prepa-joueur', for: ['joueur'] },
      { title: 'Créer un programme', sub: 'Trois niveaux, des blocs', tour: 'prepa-coach', for: ['coach', 'technique'] },
    ],
  },
  inviter: {
    label: 'Inviter',
    lead: { default: 'Quelqu’un qui veut jouer, un bénévole : un lien à envoyer depuis ton téléphone.' },
    leadFor: x => { const w = inviteWhat(x); return w.length ? `${cap(and(w))} : un lien à envoyer depuis ${x.vous ? 'votre' : 'ton'} téléphone.`
      : 'Rien à envoyer d’ici pour l’instant : pour faire venir quelqu’un qui veut jouer, parles-en au coach de l’équipe.'; },
    gestures: [
      { title: 'Inviter quelqu’un', sub: 'Par WhatsApp ou SMS', tour: 'inviter', when: x => !x.minor && (x.player || x.on('benevolat')) },
      { title: 'Inviter ton équipe', sub: 'Un lien par joueuse', tour: 'inviter-equipe', for: ['coach'], when: x => !x.benevoleHat },
    ],
  },
  avantages: {
    label: 'Avantages',
    lead: { default: 'La récompense de chaque grade de tes badges, ce que le club offre après une victoire, les offres des partenaires.' },
    minorLead: 'La récompense de chaque grade de tes badges, et ce que le club offre après une victoire.',
    vous: 'La récompense de chaque grade de vos badges, ce que le club offre après une victoire, les offres des partenaires.',
    gestures: [{ title: 'Utiliser un avantage', sub: 'Montre ton QR, le commerçant le scanne', tour: 'avantages', vous: { sub: 'Montrez votre QR, le commerçant le scanne' } },
      { title: 'Un avantage en nombre limité', sub: '« 5 en tout », premiers arrivés, premiers servis', tour: 'avantages-bureau', for: ['bureau'] }],
  },
  badges: {
    label: 'Mes badges',
    lead: {
      partenaire: 'Votre parcours Ambassade : chaque entreprise présentée au club compte.',
      default: 'Un parcours par type de mission : chaque mission le fait monter. 5 étoiles = le grade suivant ; chaque grade ouvre un cadeau du club.',
    },
    gestures: [
      { title: 'Mes badges', sub: 'Parcours, grades, récompenses', tour: 'mes-badges' },
      { title: 'Utiliser une récompense', sub: 'Garde le doigt 5 secondes, montre l’écran', tour: 'avantages', vous: { sub: 'Gardez le doigt 5 secondes, montrez l’écran' } },
    ],
  },
};
